import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useOutletContext, useNavigate, useParams } from 'react-router-dom'
import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import type { IProjectResponse } from '../../../../api/types'
import {
  useDeleteOwnProjectTeamBoardPostMutation,
  useDeleteProjectTeamBoardPostMutation,
  useGetProjectTeamSpaceQuery,
  useUpdateProjectTeamSpaceLinksMutation,
  useUpsertProjectTeamBoardPostMutation,
} from '../../../../api/projects'
import { profileApi } from '../../../../api/profile'
import { extractApiErrorMessage } from '../../../../utils/api'
import { websiteHref } from '../../../../utils/common'
import { routesProfile } from '../../../../consts'
import { NotFound } from '../../../ui-kit/NotFound'
import { parseExternalLinksInput } from '../../../../pages/Profile/EditProject/Tabs/EditDescriptionForm/helpers/externalLinks'

const BODY_MAX = 2000
const LINKS_MAX = 30
const CONTACT_MAX = 64
const FIELD_CONTROL_HEIGHT = 42

const singleLineFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: FIELD_CONTROL_HEIGHT,
    height: FIELD_CONTROL_HEIGHT,
    boxSizing: 'border-box',
  },
} as const

function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={0.75} sx={{ minWidth: 0, width: '100%' }}>
      <Typography variant="black-14-medium" component="div">
        {label}
      </Typography>
      {children}
    </Stack>
  )
}

const cardPromptStorageKey = (projectId: number) => `proka:team-space-card-prompt:${projectId}`

function renderTeamLinks(links: string[]) {
  return (
    <Stack gap={1}>
      {links.map((link) => (
        <Link
          key={link}
          href={websiteHref(link)}
          target="_blank"
          rel="noopener noreferrer"
          variant="black-14-light"
        >
          {link}
        </Link>
      ))}
    </Stack>
  )
}

function renderMessengerContact(value: string) {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) {
    return (
      <Link href={trimmed} target="_blank" rel="noopener noreferrer" variant="black-14-light">
        {trimmed}
      </Link>
    )
  }
  return (
    <Typography variant="black-14-light" sx={{ wordBreak: 'break-word' }}>
      {trimmed}
    </Typography>
  )
}

export const TeamSpace = () => {
  const project = useOutletContext<IProjectResponse>()
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { data: me } = profileApi.useGetProfileQuery()
  const canView = project.viewer?.permissions?.can_view_team_space === true
  const canEditLinks = project.viewer?.permissions?.can_edit_content === true
  const isOwner = project.viewer?.permissions?.is_owner === true
  const currentUserId = me?.id ?? null

  const { data, isLoading, isError, isFetching } = useGetProjectTeamSpaceQuery(
    { projectId: project.id },
    { skip: !canView },
  )
  const [updateLinks, updateLinksState] = useUpdateProjectTeamSpaceLinksMutation()
  const [upsertPost, upsertPostState] = useUpsertProjectTeamBoardPostMutation()
  const [deleteOwnPost, deleteOwnState] = useDeleteOwnProjectTeamBoardPostMutation()
  const [deletePost, deletePostState] = useDeleteProjectTeamBoardPostMutation()

  const [linksDraft, setLinksDraft] = useState<string | null>(null)
  const [bodyDraft, setBodyDraft] = useState<string | null>(null)
  const [telegramDraft, setTelegramDraft] = useState<string | null>(null)
  const [showCardPrompt, setShowCardPrompt] = useState(false)
  const [isEditingLinks, setIsEditingLinks] = useState(false)
  const [isEditingOwnCard, setIsEditingOwnCard] = useState(false)

  const linksText =
    linksDraft ??
    (data?.links?.length ? data.links.join('\n') : '')

  const myPost = useMemo(() => {
    if (!data?.posts?.length || currentUserId == null) return null
    return data.posts.find((p) => p.user_id === currentUserId) ?? null
  }, [data?.posts, currentUserId])

  const cardBody = bodyDraft ?? myPost?.body ?? ''
  const cardContact = telegramDraft ?? myPost?.telegram ?? ''

  const hasOwnCard = Boolean(myPost?.body?.trim() || myPost?.telegram?.trim())
  const otherPosts = useMemo(() => {
    if (!data?.posts?.length || currentUserId == null) return data?.posts ?? []
    return data.posts.filter((p) => p.user_id !== currentUserId)
  }, [data?.posts, currentUserId])

  const startEditLinks = () => {
    setLinksDraft(data?.links?.length ? data.links.join('\n') : '')
    setIsEditingLinks(true)
  }

  const cancelEditLinks = () => {
    setLinksDraft(null)
    setIsEditingLinks(false)
  }

  const startEditOwnCard = () => {
    setBodyDraft(myPost?.body ?? '')
    setTelegramDraft(myPost?.telegram ?? '')
    setIsEditingOwnCard(true)
  }

  const cancelEditOwnCard = () => {
    setBodyDraft(null)
    setTelegramDraft(null)
    setIsEditingOwnCard(false)
  }

  useEffect(() => {
    if (!canView || !data) return
    if (myPost) {
      setShowCardPrompt(false)
      return
    }
    try {
      const dismissed = localStorage.getItem(cardPromptStorageKey(project.id)) === '1'
      setShowCardPrompt(!dismissed)
    } catch {
      setShowCardPrompt(true)
    }
  }, [canView, data, myPost, project.id])

  const dismissCardPrompt = () => {
    try {
      localStorage.setItem(cardPromptStorageKey(project.id), '1')
    } catch {
      // ignore
    }
    setShowCardPrompt(false)
  }

  if (!canView) {
    return <NotFound text="Командное пространство доступно только участникам проекта" />
  }

  if (isLoading || (isFetching && !data)) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    )
  }

  if (isError || !data) {
    return <NotFound text="Не удалось загрузить командное пространство" />
  }

  const handleSaveLinks = async () => {
    const rawLines = linksText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const parsed = parseExternalLinksInput(linksText)

    if (rawLines.length > 0 && parsed.length === 0) {
      toast.error(
        'Не удалось распознать ссылки. Укажите https://… или домен (example.com, t.me/…).',
      )
      return
    }

    const unrecognized = rawLines.filter((line) => parseExternalLinksInput(line).length === 0)
    if (unrecognized.length > 0) {
      toast.error(
        'Не удалось распознать ссылки. Укажите https://… или домен (example.com, t.me/…).',
      )
      return
    }

    if (parsed.length > LINKS_MAX) {
      toast.error(`Не более ${LINKS_MAX} ссылок.`)
      return
    }

    try {
      await updateLinks({ projectId: project.id, links: parsed }).unwrap()
      setLinksDraft(null)
      setIsEditingLinks(false)
      toast.success('Ссылки команды сохранены')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить ссылки')
    }
  }

  const handleSaveCard = async () => {
    const body = cardBody.trim()
    const contact = cardContact.trim()
    if (!body && !contact) {
      toast.info('Укажите контакт в мессенджере или короткое сообщение для команды')
      return
    }
    try {
      await upsertPost({
        projectId: project.id,
        body,
        telegram: contact || null,
      }).unwrap()
      setBodyDraft(null)
      setTelegramDraft(null)
      setIsEditingOwnCard(false)
      setShowCardPrompt(false)
      toast.success('Карточка сохранена')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить карточку')
    }
  }

  const handleDeleteOwn = async () => {
    try {
      await deleteOwnPost({ projectId: project.id }).unwrap()
      setBodyDraft(null)
      setTelegramDraft(null)
      setIsEditingOwnCard(false)
      toast.success('Карточка удалена')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось удалить карточку')
    }
  }

  const handleDeleteOther = async (userId: number) => {
    try {
      await deletePost({ projectId: project.id, userId }).unwrap()
      toast.success('Карточка удалена')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось удалить карточку')
    }
  }

  return (
    <Stack gap={2.5} pb={4}>
      <Stack gap={0.75}>
        <Typography variant="black-16-semiBold">Командное пространство</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          Это доска для участников проекта, не переписка. Здесь — общие ссылки для общения и короткие
          карточки друг для друга (например, контакты).
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => navigate(`/${routesProfile.PROJECTS.path}/${slug ?? project.slug}`)}
          sx={{ alignSelf: 'flex-start', px: 0 }}
        >
          ← К проекту
        </Button>
      </Stack>

      {showCardPrompt ? (
        <Card variant="primaryAdaptiveCard" sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Stack gap={1}>
            <Typography variant="black-14-medium">Заполните карточку для команды</Typography>
            <Typography variant="black-14-light" color="text.secondary">
              Добавьте контакт в мессенджере и короткое сообщение — так участникам проще с вами связаться.
              Это не чат.
            </Typography>
            <Stack direction="row" gap={1}>
              <Button variant="follow" size="small" onClick={dismissCardPrompt}>
                Понятно
              </Button>
            </Stack>
          </Stack>
        </Card>
      ) : null}

      <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
        <Stack gap={1.5}>
          <Typography variant="black-14-medium">Ссылки команды</Typography>
          <Typography variant="black-12-light" color="text.secondary">
            Общий чат, документы — видно только участникам. По одной ссылке на строку (до {LINKS_MAX}).
            Редактируют владелец и администраторы.
          </Typography>
          {canEditLinks ? (
            data.links.length > 0 && !isEditingLinks ? (
              <>
                {renderTeamLinks(data.links)}
                <Stack direction="row">
                  <Button variant="follow" onClick={startEditLinks}>
                    Редактировать
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <TextField
                  multiline
                  minRows={3}
                  value={linksText}
                  onChange={(e) => setLinksDraft(e.target.value)}
                  placeholder={'https://...\nhttps://...'}
                  size="small"
                  fullWidth
                />
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    variant="follow"
                    onClick={() => void handleSaveLinks()}
                    disabled={updateLinksState.isLoading}
                  >
                    Сохранить ссылки
                  </Button>
                  {data.links.length > 0 ? (
                    <Button variant="text" onClick={cancelEditLinks}>
                      Отмена
                    </Button>
                  ) : null}
                </Stack>
              </>
            )
          ) : data.links.length === 0 ? (
            <Typography variant="black-14-light" color="text.secondary">
              Пока нет общих ссылок. Их может добавить автор или администратор проекта.
            </Typography>
          ) : (
            renderTeamLinks(data.links)
          )}
        </Stack>
      </Card>

      <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
        <Stack gap={1.5}>
          <Typography variant="black-14-medium">Моя карточка для команды</Typography>
          <Typography variant="black-12-light" color="text.secondary">
            Мессенджер / контакт — свободно (ник, ссылка, что удобно команде). Сообщение — до {BODY_MAX}{' '}
            символов. Нужно хотя бы одно из полей.
          </Typography>
          {hasOwnCard && !isEditingOwnCard ? (
            <>
              <Stack gap={0.5}>
                {myPost?.telegram ? renderMessengerContact(myPost.telegram) : null}
                {myPost?.body ? (
                  <Typography variant="black-14-light" sx={{ whiteSpace: 'pre-wrap' }}>
                    {myPost.body}
                  </Typography>
                ) : null}
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button variant="follow" onClick={startEditOwnCard}>
                  Редактировать
                </Button>
                <Button
                  variant="danger"
                  onClick={() => void handleDeleteOwn()}
                  disabled={deleteOwnState.isLoading}
                >
                  Удалить свою
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <FieldBlock label="Мессенджер / контакт">
                <TextField
                  size="small"
                  fullWidth
                  value={cardContact}
                  onChange={(e) => setTelegramDraft(e.target.value.slice(0, CONTACT_MAX))}
                  placeholder="ник или ссылка"
                  inputProps={{ maxLength: CONTACT_MAX, 'aria-label': 'Мессенджер / контакт' }}
                  sx={singleLineFieldSx}
                />
              </FieldBlock>
              <FieldBlock label="Сообщение">
                <TextField
                  multiline
                  minRows={3}
                  value={cardBody}
                  onChange={(e) => setBodyDraft(e.target.value.slice(0, BODY_MAX))}
                  placeholder="Роль, удобное время для созвонов…"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: BODY_MAX, 'aria-label': 'Сообщение' }}
                />
              </FieldBlock>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="follow"
                  onClick={() => void handleSaveCard()}
                  disabled={upsertPostState.isLoading}
                >
                  Сохранить карточку
                </Button>
                {hasOwnCard ? (
                  <Button variant="danger" onClick={cancelEditOwnCard}>
                    Отмена
                  </Button>
                ) : null}
              </Stack>
            </>
          )}
        </Stack>
      </Card>

      <Stack gap={1.5}>
        <Typography variant="black-14-medium">Карточки участников</Typography>
        {otherPosts.length === 0 ? (
          <Typography variant="black-14-light" color="text.secondary">
            Пока никто не оставил сообщение. Будьте первым.
          </Typography>
        ) : (
          otherPosts.map((post) => {
            const displayName =
              post.user?.fullname?.trim() ||
              post.user?.shortname?.trim() ||
              (post.user?.slug ? `@${post.user.slug}` : `ID ${post.user_id}`)
            return (
              <Card key={post.user_id} variant="primaryAdaptiveCard" sx={{ p: 2 }}>
                <Stack direction="row" gap={1.5} alignItems="flex-start">
                  <Avatar src={post.user?.avatar ?? undefined} sx={{ width: 40, height: 40 }} />
                  <Stack minWidth={0} flex={1} gap={0.5}>
                    <Typography variant="black-14-medium">{displayName}</Typography>
                    {post.telegram ? renderMessengerContact(post.telegram) : null}
                    {post.body ? (
                      <Typography variant="black-14-light" sx={{ whiteSpace: 'pre-wrap' }}>
                        {post.body}
                      </Typography>
                    ) : null}
                  </Stack>
                  {isOwner ? (
                    <Button
                      variant="danger"
                      size="small"
                      disabled={deletePostState.isLoading}
                      onClick={() => void handleDeleteOther(post.user_id)}
                    >
                      Удалить
                    </Button>
                  ) : null}
                </Stack>
              </Card>
            )
          })
        )}
      </Stack>
    </Stack>
  )
}
